import type { TenantChoice } from '../auth/AuthProvider';

/**
 * How the tenant switcher groups what a session can reach.
 *
 * Its own module rather than living beside the component, because it is the part
 * worth testing directly: the property that matters is structural — every
 * destination appears exactly once, and nothing a person can enter is dropped.
 */
export interface TenantGroup {
  parent: TenantChoice | null;
  children: TenantChoice[];
}

export function groupTenantChoices(choices: TenantChoice[]): TenantGroup[] {
  const parents = choices.filter((c) => c.isParent);
  const groups: TenantGroup[] = parents.map((parent) => ({
    parent,
    children: choices.filter((c) => !c.isParent && c.parentTenantId === parent.tenantId),
  }));

  /*
   * Anything not under a parent this person can see: a plain tenant membership,
   * or a sub-tenant whose parent they are not a member of. Listed on its own
   * rather than dropped — it is somewhere they can actually go, and a switcher
   * that silently omits a destination is worse than an ungrouped one.
   */
  const claimed = new Set(
    groups.flatMap((g) => [g.parent!.tenantId, ...g.children.map((c) => c.tenantId)]),
  );
  for (const c of choices) {
    if (!claimed.has(c.tenantId)) groups.push({ parent: null, children: [c] });
  }
  return groups;
}

/**
 * The sub-tenants a vehicle in `tenantId` could be moved TO.
 *
 * Siblings only: the same organisation, never the parent (which holds no
 * operational data), and never the branch it is already in. Empty for a
 * single-branch customer, which is most of them — and the Move action is hidden
 * entirely rather than offered with nothing behind it.
 *
 * ACTIVE only. `transferToSibling` refuses a destination that is not active, so
 * a suspended branch in this list is a choice whose only possible outcome is a
 * 400 — the operator picks it, presses Move, and is told no. Consequence worth
 * knowing: when the ONLY sibling is suspended this returns empty and the Move
 * action disappears, which is the same thing the single-branch case does.
 */
export function siblingSubTenants(
  choices: TenantChoice[],
  tenantId: string | null,
): TenantChoice[] {
  const here = choices.find((c) => c.tenantId === tenantId);
  if (!here || here.isParent || here.parentTenantId === null) return [];
  return choices.filter(
    (c) =>
      !c.isParent &&
      c.parentTenantId === here.parentTenantId &&
      c.tenantId !== tenantId &&
      c.state === 'ACTIVE',
  );
}
